// The original transcript for the transcript view, loaded on demand: the copy in memory (e.g.
// the one the summary run fetched in the browser), else the one the server stored with the
// summary (GET /api/transcript), else from YouTube like for a summary: signed-in users in server
// mode via POST /api/transcript (falling back to the browser when YouTube blocks the server),
// everyone else in the browser.

import type { SummaryLang, TranscriptMode, VideoTranscript } from '../../shared/api';
import { ApiRequestError, getServerTranscript, getStoredTranscript, isAbortError } from './api';
import { fetchTranscriptInBrowser } from './transcript';

const MAX_CACHED = 10;
/** Most recently used last. */
const cache = new Map<string, VideoTranscript>();

export function rememberTranscript(transcript: VideoTranscript): void {
  cache.delete(transcript.videoId);
  cache.set(transcript.videoId, transcript);
  while (cache.size > MAX_CACHED) cache.delete(cache.keys().next().value!);
}

export function cachedTranscript(videoId: string): VideoTranscript | undefined {
  return cache.get(videoId);
}

/** Server failures after which the browser tries itself. */
const BROWSER_FALLBACK: ReadonlySet<string> = new Set(['youtube_blocked', 'rate_limited', 'login_required', 'internal']);

export async function loadTranscript(
  videoId: string,
  options: { mode: TranscriptMode; lang?: SummaryLang | undefined; signal?: AbortSignal },
): Promise<VideoTranscript> {
  const cached = cachedTranscript(videoId);
  if (cached) return cached;
  let transcript: VideoTranscript | undefined;
  try {
    transcript = (await getStoredTranscript(videoId, options.signal)) ?? undefined;
  } catch (error) {
    if (isAbortError(error)) throw error; // otherwise: fetch it from YouTube instead
  }
  if (!transcript && options.mode === 'server') {
    try {
      transcript = await getServerTranscript(videoId, options.lang, options.signal);
    } catch (error) {
      if (!(error instanceof ApiRequestError && BROWSER_FALLBACK.has(error.code))) throw error;
    }
  }
  transcript ??= await fetchTranscriptInBrowser(videoId, {
    ...(options.signal ? { signal: options.signal } : {}),
    ...(options.lang ? { preferredLang: options.lang } : {}),
  });
  rememberTranscript(transcript);
  return transcript;
}
